import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiHelper } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

// Query keys factory
export const employeeKeys = {
  all: ['employees'],
  lists: () => [...employeeKeys.all, 'list'],
  list: (orgId) => [...employeeKeys.lists(), orgId],
  details: () => [...employeeKeys.all, 'detail'],
  detail: (id) => [...employeeKeys.details(), id],
  me: () => [...employeeKeys.all, 'me'],
};

// ===== Get current employee basic info =====
// GET /api/emp/employees/me/
export function useMyProfile(enabled = true) {
  const { user } = useAuth();
  return useQuery({
    queryKey: employeeKeys.me(),
    queryFn: () => apiHelper.getResource('/emp/employees/me/'),
    enabled: !!enabled && !!user && !user.is_superuser && !!user.employeeId,
  });
}

// ===== Update current employee basic info =====
// PATCH /api/emp/employees/me/
export function useUpdateMyProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => apiHelper.patchResource('/emp/employees/me/', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.me() });
    },
  });
}

// ===== Change current user's password =====
// POST /api/auth/password/reset/
export function useChangePassword() {
  return useMutation({
    mutationFn: (data) => apiHelper.postResource('/auth/password/reset/', data),
  });
}

// Fetch all employees for an organization
export function useEmployees(orgId, enabled = true) {
  return useQuery({
    queryKey: orgId ? employeeKeys.list(orgId) : employeeKeys.all,
    queryFn: () => {
      const options = orgId ? { orgId } : {};
      return apiHelper.getResource('/emp/employees/', options);
    },
    enabled,
    select: (data) => {
      const employees = Array.isArray(data) ? data : (data?.results || []);
      return employees.map(emp => ({
        ...emp,
        name: `${emp.first_name} ${emp.last_name}`.trim(),
        role: emp.job_title,
        employeeId: emp.employee_no,
        // Ensure other fields are passed through or mapped as needed
        avatar: emp.avatar || 'https://via.placeholder.com/40', // Default avatar
        projectAssignments: emp.project_assignments || []
      }));
    }
  });
}

// Fetch single employee
export function useEmployee(id, orgId) {
  return useQuery({
    queryKey: employeeKeys.detail(id),
    queryFn: () => apiHelper.getResource(`/emp/employees/${id}/`, { orgId }),
    enabled: !!id && !!orgId,
  });
}

// Create employee with user account
export function useCreateEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data, orgId }) =>
      apiHelper.postResource('/emp/employees/', data, { orgId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
    },
  });
}

// Update employee
export function useUpdateEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data, orgId }) =>
      apiHelper.putResource(`/emp/employees/${id}/`, data, { orgId }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: employeeKeys.detail(id) });
    },
  });
}

// Delete employee
export function useDeleteEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, orgId }) =>
      apiHelper.deleteResource(`/emp/employees/${id}?confirm=yes`, { orgId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
    },
  });
}
// Fetch line manager options based on hierarchy
// Supports ?search= for name-based search
export function useLineManagerOptions({ roleId, departmentId, unitId, orgId, search }) {
  return useQuery({
    queryKey: [...employeeKeys.lists(), 'line-managers-options', { roleId, departmentId, unitId, orgId, search }],
    queryFn: () => {
      const params = {};
      if (roleId) params.role_id = roleId;
      if (departmentId) params.department_id = departmentId;
      if (unitId) params.unit_id = unitId;
      if (orgId) params.org_id = orgId;
      if (search) params.search = search;

      return apiHelper.getResource('/emp/line-manager-options/', { params, orgId });
    },
    enabled: !!orgId && !!roleId,
    select: (data) => {
      const managers = data?.managers || (Array.isArray(data) ? data : (data?.results || []));
      return managers.map(m => ({
        id: m.id,
        name: m.name || m.full_name,
        role: m.role_name || m.job_title || m.role,
        employeeId: m.employee_no,
        department: m.department,
        department_name: m.department_name,
        unit: m.unit,
        unit_name: m.unit_name
      }));
    }
  });
}

// Grant cross-org access after employee creation
// POST /api/emp/cross-org-access/
export function useCrossOrgAccess() {
  return useMutation({
    mutationFn: ({ managerEmployeeId, reportEmployeeId, orgId }) =>
      apiHelper.postResource('/emp/cross-org-access/', {
        manager_employee_id: managerEmployeeId,
        report_employee_id: reportEmployeeId,
        scopes: ['LVE', 'TS', 'PDP'],
      }, { orgId }),
  });
}

// Fetch cross-organization employees
// GET /api/emp/cross-org-employees/
export function useCrossOrgEmployees({ orgId, search, enabled = false } = {}) {
  return useQuery({
    queryKey: ['cross-org-employees', { orgId, search }],
    queryFn: () => {
      const params = {};
      if (search) params.search = search;
      return apiHelper.getResource('/emp/cross-org-employees/', { params, orgId });
    },
    enabled: !!orgId && enabled,
    select: (data) => {
      const list = data?.employees || (Array.isArray(data) ? data : (data?.results || []));
      return list.map(m => ({
        id: m.id,
        name: m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim(),
        role: m.job_title || m.role || '',
        organization: m.org_name || m.organization_name || m.organization || '',
        employeeId: m.employee_no || '',
      }));
    }
  });
}
// Assign project to employee
export function useEmployeeProjects(orgId) {
  return useQuery({
    queryKey: ['employee-projects', orgId],
    queryFn: () => {
      const options = orgId ? { orgId } : {};
      return apiHelper.getResource('/emp/employee-projects/', options);
    },
    enabled: !!orgId,
    select: (data) => Array.isArray(data) ? data : (data?.results || [])
  });
}

// Assign project to employee
export function useCreateEmployeeProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data, orgId }) =>
      apiHelper.postResource('/emp/employee-projects/', data, { orgId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
    },
  });
}

// Fetch direct reports for the logged-in manager
// GET /api/emp/direct-reports/?org_id=xxx
export function useDirectReports(orgId) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['direct-reports', orgId],
    queryFn: () => apiHelper.getResource(`/emp/direct-reports/?org_id=${orgId}`),
    enabled: !!orgId && !!user?.employeeId,
    select: (data) => {
      const list = Array.isArray(data) ? data : (data?.results || []);
      return list.map(emp => ({
        ...emp,
        first_name: emp.first_name || '',
        last_name: emp.last_name || '',
        job_title: emp.job_title || emp.role || '',
        employee_no: emp.employee_no || '',
      }));
    },
  });
}

// Fetch roles available for an org — used during employee creation
export function useOrgRoles(orgId) {
  return useQuery({
    queryKey: ['org-roles', orgId],
    queryFn: () => apiHelper.getResource(`/rbac/org/roles/${orgId}/`),
    enabled: !!orgId,
    retry: false,
  });
}
// Download all employees as Excel
export function useDownloadEmployees() {
  return useMutation({
    mutationFn: ({ orgId }) => {
      const filename = `employees_${new Date().toISOString().split('T')[0]}.xlsx`;
      return apiHelper.downloadResource(
        '/emp/employees/download/',
        filename,
        { orgId }
      );
    },
  });
}

// HR Employee Snapshot — full dashboard view of a single employee
export function useEmployeeSnapshot(employeeId) {
  return useQuery({
    queryKey: ['employee-snapshot', employeeId],
    queryFn: () => apiHelper.getResource(`/emp/employee-snapshot/${employeeId}/`),
    enabled: !!employeeId,
  });
}
